'use client';

import { useContext, useState, useMemo } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { useMutation } from 'react-query';

import { Row } from '@guestyci/foundation/Layout';
import { FormProvider } from '@guestyci/foundation/enums';
import Form from '@guestyci/foundation/Form';
import FormField from '@guestyci/foundation/FormField';
import { email } from '@guestyci/foundation/validators';
import createStyles from '@guestyci/foundation/createStyles';
import Spinner from '@guestyci/foundation/Spinner';
import t from '@guestyci/localize/t.macro/t.macro';
import { Button, Heading, Input, Textarea, Text } from '@guestyci/arc';

import ContactFormSuccess from 'components/ContactFormSuccess/ContactFormSuccess';
import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';
import { getWindow, getDocument } from 'utils/isBrowser';
import contactUs from 'api/contact/contactUs';

const focusFirstError = (errors) => {
  const doc = getDocument();
  const fieldOrder = ['name', 'email', 'subject', 'message'];
  const firstInvalidField = fieldOrder.find((fieldName) => errors?.[fieldName]);

  if (!firstInvalidField) return;

  const element = doc.getElementById(`contact-${firstInvalidField}`);
  element?.focus();
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const useStyles = createStyles(({ breakpoints: { create } }) => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  formRoot: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 640,
    '& [class*="Input-root"]': {
      background: '#fff',
    },
  },
  inputGroup: {
    justifyContent: 'space-between',
    marginBottom: 28,
    '& div': {
      width: '100%',
      [create('xs')]: {
        maxWidth: '100%',
      },
      [create('lg')]: {
        maxWidth: 305,
      },
    },
    [create('xs')]: {
      flexDirection: 'column',
      marginBottom: 0,
      '& > div': {
        marginBottom: 28,
      },
    },
    [create('lg')]: {
      flexDirection: 'row',
    },
  },
  loaderWrapper: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f5f5f5b0',
  },
}));

const useContactUs = () => useMutation(contactUs);

const ContactForm = ({ wrapper, isStandAlonePage = false }) => {
  const { root, formRoot, inputGroup, loaderWrapper } = useStyles();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const { displayOptions: { shouldShowContactUsForm } = {}, contentConfiguration: { sections } = {} } =
    useContext(WebsiteSettingsContext);
  const renderContactForm = useMemo(() => !!sections?.CONTACT?.active, [sections]);
  const win = getWindow();

  const { mutate: handleSubmitContactUs, isLoading: isSubmittingContactUs } = useContactUs();

  const onSubmit = (values) => {
    win.scrollTo({ top: 0, behavior: 'smooth' });
    handleSubmitContactUs(
      {
        params: values,
      },
      {
        onSuccess: () => setIsSuccessModalOpen(true),
      }
    );
  };

  if (!shouldShowContactUsForm && !renderContactForm) {
    return null;
  }

  return (
    <div className={wrapper}>
      <div className={root}>
        {!isSuccessModalOpen && (
          <FinalForm
            onSubmit={onSubmit}
            initialValues={{}}
            provider={FormProvider.Final}
            render={({ handleSubmit, form }) => {
              const onFormSubmit = (event) => {
                const { errors } = form.getState();
                focusFirstError(errors);
                handleSubmit(event);
              };

              return (
                <Form
                  title="contacts"
                  onSubmit={onFormSubmit}
                  provider={FormProvider.Final}
                  fieldInstance={Field}
                  className={formRoot}
                >
                  <Heading
                    className="gst-text-foreground gst-mb-2 gst-self-center"
                    variant={isStandAlonePage ? 'h1' : 'h2'}
                  >
                    {t('Contact us')}
                  </Heading>
                  <Text variant="h5" className="mb-3">
                    {t('Fields marked with * are required')}
                  </Text>
                  <Row className={inputGroup}>
                    <FormField name="name" label={t('Name *')} validate={[(value) => !value && t('Name is required')]}>
                      <Input id="contact-name" aria-label={t('Name')} className="gst-bg-white" />
                    </FormField>
                    <FormField
                      name="email"
                      label={t('Email *')}
                      validate={[email, (value) => !value && t('Email is required')]}
                    >
                      <Input
                        id="contact-email"
                        title={t('Email')}
                        aria-label={t('Email')}
                        type="email"
                        className="gst-bg-white"
                      />
                    </FormField>
                  </Row>
                  <FormField
                    name="subject"
                    label={t('Subject *')}
                    validate={[(value) => !value && t('Subject is required')]}
                    className="gst-mb-7"
                  >
                    <Input id="contact-subject" aria-label={t('Subject')} className="gst-bg-white" />
                  </FormField>
                  <FormField
                    name="message"
                    label={t('Message *')}
                    className="gst-mb-7"
                    validate={[(value) => !value && t('Message is required')]}
                  >
                    <Textarea
                      id="contact-message"
                      title={t('Message')}
                      aria-label={t('Message')}
                      minRows={7}
                      maxRows={7}
                      className="gst-bg-white gst-min-h-40 gst-min-h-48"
                    />
                  </FormField>
                  <Button type="submit" className="gst-max-w-40 gst-self-end btn-colored">
                    {t('Submit')}
                  </Button>
                </Form>
              );
            }}
          />
        )}
        {isSubmittingContactUs && (
          <div className={loaderWrapper}>
            <Spinner />
          </div>
        )}
        {isSuccessModalOpen && <ContactFormSuccess onClose={() => setIsSuccessModalOpen(false)} />}
      </div>
    </div>
  );
};

export default ContactForm;
